/* lib-pack-18 · the sign-in seam (WF-047; the connexion ruling 19/10). The merchant app's side of the auth routes of
   apps/api src/routes/app.js (contract 01 §1.1–1.3): a magic link is asked for an e-mail, its token arrives on
   /connexion#token=… and is exchanged at once for the session cookie, the fragment dropped from the address bar (01
   §1.2); GET /v1/app/me says who is signed in and lib/scope's role gates read that user; « Se déconnecter » revokes the
   session. The screens (routes/connexion, Builder B) call these and say nothing the API does not: the magic-link answer is
   always 202, so no sentence of the app says whether an address has an espace. The demo (no VITE_API_URL) answers the
   same calls from mocks/handlers. No Google button on the merchant side in V1 (the ruling). */
import { ApiError, call } from './api'
import { setCurrentUser, type CurrentUser, type Role } from './scope'
import { PLAN_LABELS, setPlan, type Plan } from './campaign'

/* ---------- the shapes (01 §1.3) ---------- */
export interface MeUser { id: string; name: string; email: string | null; role: Role; locations: string[] }
export interface MeWorkspace { id: string; name: string; plan: string; planStatus: string; trialEndsAt: string | null }
export interface Me { user: MeUser; workspace: MeWorkspace; workspaces: { id: string; name: string }[] }
/** What POST /app/auth/magic answers: `me`, and `chooseWorkspace` when the e-mail has live logins in several workspaces
    (the session opens in the most recently used one — V1 is one espace, the app opens it). */
export interface SignedIn { me: Me; chooseWorkspace?: boolean }

/* ---------- the doors ---------- */
export const SIGN_IN = '/connexion'
export const SIGN_UP = '/creer-mon-espace'
/** The two doors live outside the shell and the mirror (App.tsx): a lost session there is said, never redirected. */
export const isAuthPath = (pathname: string): boolean => pathname === SIGN_IN || pathname === SIGN_UP
/** Where a lost session goes: /connexion, keeping the page the merchant wanted as `?suite=` so the sign-in brings them back. */
export function signInPath(next?: string | null): string {
  const n = safeSuite(next)
  return n === '/' ? SIGN_IN : `${SIGN_IN}?suite=${encodeURIComponent(n)}`
}
/** `?suite=` read back after the sign-in: an in-app path only (starts with one « / »), never another origin; the doors
    themselves never (no loop); « / » otherwise. */
export function suiteOf(search: string): string {
  try { return safeSuite(new URLSearchParams(search).get('suite')) } catch { return '/' }
}
function safeSuite(p: string | null | undefined): string {
  if (!p || !p.startsWith('/') || p.startsWith('//') || /[\\\s]/.test(p)) return '/'
  const path = p.split(/[?#]/)[0]
  return isAuthPath(path) ? '/' : p
}

/* ---------- the user ---------- */
/** The server's user as lib/scope keeps it (01 §1.3: « user is exactly the app's CurrentUser, plus id and email »). */
export const userOf = (me: Me): CurrentUser => ({ role: me.user.role, name: me.user.name, locations: me.user.locations })
/** lib-pack-19 · the workspace's plan is the server's (01 §1.3 workspace.plan): the builder's plan gates (lib/campaign
    getPlan, planAllows) read it, not a world's default — « Offre gratuite » on a Starter workspace hid the builder's options.
    A plan the app does not know (none expected) leaves the gates as they were. */
const keepPlan = (me: Me): void => { const p = me.workspace?.plan; if (p && p in PLAN_LABELS) setPlan(p as Plan) }
/** Who is signed in (GET /v1/app/me): the role gates of the app read this user (lib/scope) once lib/server has it. */
export async function readMe(): Promise<Me> {
  const me = await call<Me>('GET', '/app/me')
  setCurrentUser(userOf(me)); keepPlan(me)
  return me
}

/* ---------- the magic link ---------- */
/** POST /v1/app/auth/magic-link { email } — always 202: the screen says the same sentence whatever the address. 400
    invalid_email (field email) and 429 rate_limited come back as ApiError (authLines says them). */
export async function requestMagicLink(email: string): Promise<void> {
  await call<Record<string, never>>('POST', '/app/auth/magic-link', { email: email.trim() })
}
/** The token of a sign-in or invitation link (`#token=…` on /connexion or /creer-mon-espace), dropped from the address
    bar as soon as it is read (01 §1.2) so no reload, bookmark or screenshot keeps it. */
export function readFragmentToken(): string | null {
  try {
    const m = /(?:^#|&)token=([^&]+)/.exec(location.hash)
    if (!m) return null
    history.replaceState(history.state, '', location.pathname + location.search)
    return decodeURIComponent(m[1])
  } catch { return null }
}
/** POST /v1/app/auth/magic { token } — single use: the cookie is set by the answer, the user kept for the role gates.
    410 link_expired / 410 invite_expired come back as ApiError (authLines). */
export async function exchangeToken(token: string): Promise<SignedIn> {
  const r = await call<SignedIn>('POST', '/app/auth/magic', { token })
  setCurrentUser(userOf(r.me)); keepPlan(r.me)
  return r
}

/* ---------- the way out ---------- */
/** POST /v1/app/auth/logout → 204, the cookie cleared; then the sign-in door. The kept user is dropped first, so a
    refused logout (the session was already gone) still lands on /connexion with nothing of the person left here. */
export async function signOut(): Promise<void> {
  setCurrentUser(null)
  try { await call<void>('POST', '/app/auth/logout') } catch { /* already out */ }
  location.assign(SIGN_IN)
}

/* ---------- the sentences ---------- */
/** Why a sign-in step did not go through, in the API's words where it has them (the 410 lines are the contract's) and
    the app's otherwise; the rate limit and an unreadable address are said as such. */
export function authLines(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Le serveur ne répond pas\u00a0: rien n’a changé. Vérifiez la connexion, puis réessayez.'
  switch (e.code) {
    case 'link_expired': return 'Ce lien a expiré ou a déjà servi. Demandez-en un nouveau.'
    case 'invite_expired': return 'Cette invitation a expiré ou a été annulée. Demandez-en une nouvelle à votre responsable.'
    case 'invalid_email': return e.message || 'Vérifiez l’adresse e-mail.'
    case 'rate_limited': return e.message || 'Trop de demandes en peu de temps\u00a0: patientez quelques minutes, puis réessayez.'
  }
  if (e.status === 401) return 'Votre session a expiré\u00a0: reconnectez-vous, puis réessayez.'
  return e.message || 'L’action n’a pas abouti\u00a0: rien n’a changé. Réessayez dans un instant.'
}
